import React from 'react';
import { Typography, TypographyProps, Box, useTheme } from '@mui/material';
import { markDownToHtml } from '@europrocurement/l2d-utils';

export interface NoteContentProps {
    content: string;
    enableMarkdown?: boolean;
    variant?: TypographyProps['variant'];
    sx?: TypographyProps['sx'];
}

export const NoteContent: React.FunctionComponent<NoteContentProps> = function ({
    content,
    enableMarkdown = true,
    variant = 'caption',
    sx,
}) {
    const theme = useTheme();
    const formattedContent = React.useMemo(() => {
        if (!content) return '';

        if (enableMarkdown) {
            return markDownToHtml({ content });
        }
        // Mode texte simple avec sauts de ligne
        return content
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>');
    }, [content, enableMarkdown]);

    if (enableMarkdown) {
        return (
            <Box
                sx={{
                    '& p': {
                        margin: '0 0 1em 0',
                        '&:last-child': { margin: 0 },
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                        margin: '1.5em 0 0.5em 0',
                        fontWeight: 600,
                        '&:first-child': { marginTop: 0 },
                    },
                    '& h1': { fontSize: '1.4em' },
                    '& h2': { fontSize: '1.3em' },
                    '& h3': { fontSize: '1.2em' },
                    '& ul, & ol': {
                        margin: '0 0 1em 0',
                        paddingLeft: '1.5em',
                    },
                    '& li': {
                        marginBottom: '0.2em',
                    },
                    '& blockquote': {
                        borderLeft: `4px solid ${theme.palette.grey[300]}`,
                        paddingLeft: '1em',
                        margin: '1em 0',
                        fontStyle: 'italic',
                        color: 'text.secondary',
                    },
                    '& code': {
                        backgroundColor: 'grey.100',
                        padding: '0.2em 0.4em',
                        borderRadius: '3px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em',
                    },
                    '& pre': {
                        backgroundColor: 'grey.100',
                        padding: '1em',
                        borderRadius: '5px',
                        overflow: 'auto',
                        margin: '1em 0',
                        '& code': {
                            background: 'none',
                            padding: 0,
                        },
                    },
                    '& strong, & b': {
                        fontWeight: 600,
                    },
                    '& a': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': {
                            color: 'primary.dark',
                            textDecoration: 'none',
                        },
                        '&:visited': {
                            color: 'primary.main',
                        },
                    },
                    lineHeight: 1.6,
                    wordBreak: 'break-word',
                    ...sx,
                }}
                dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
        );
    }

    return (
        <Typography
            variant={variant}
            sx={{
                lineHeight: 1.6,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                ...sx,
            }}
            dangerouslySetInnerHTML={{ __html: formattedContent }}
        />
    );
};
